// @ vendor
const Immutable = require('immutable');
const map = require('lodash/collection/map');
const get = require('lodash/object/get');
const sortBy = require('lodash/collection/sortBy');
const { toCamelCase } = require('utilities/stringHelper');

// @ constants
const {
    FLOW_STEPS,
    LENGTH_POSTAL_CODE,
    PROPERTY_CHARACTER,
    PROPERTY_USAGE_RELATED,
    SIZE_SLIDER_CONFIG,
} = require('../../insuranceConstants/insuranceRequestHome');

const {
    INSURANCE_REQUEST_CHANGE_ERROR_POSTAL_CODE,
    INSURANCE_REQUEST_CHANGE_ERROR_POPULATION,
    INSURANCE_REQUEST_CLEAR_POSTAL_CODE_ERROR,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_LOAD_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_RESET_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_VALIDATE_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_VISIBLE_STEP,
    INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_DATA,
    INSURANCE_REQUEST_RELATED_RNC_SIMULATION_LINK_S3_CLIENT_SUCCESS,
    INSURANCE_REQUEST_RELATED_RNC_POST_APPRAISAL_WIZARD_SET_DATA,
    INSURANCE_REQUEST_RELATED_RNC_SELECTED_PLAN,
    INSURANCE_REQUEST_RELATED_RNC_SET_MIN_CONTINENT_VALUE,
    INSURANCE_REQUEST_SELECT_POPULATION_REQUEST,
    INSURANCE_REQUEST_SELECT_POPULATION_SUCCESS,
    INSURANCE_REQUEST_SELECT_POPULATION_FAILURE,
} = require('../../insuranceConstants/actionTypes');

const setInitialState = () => {
    return Immutable.Map().merge({
        content: 0,
        continent: 0,
        fieldErrors: false,
        immPostalcode: '',
        isFetchingSelectPopulation: false,
        minContinentValue: 0,
        population: null,
        populationError: false,
        populationLabel: '',
        populationOptions: [],
        populationValue: null,
        postAppraisalFetchRecommendation: true,
        postalCode: '',
        postalCodeError: false,
        propertyCharacter: PROPERTY_CHARACTER.OWNER,
        propertyType: '',
        propertyUsage: PROPERTY_USAGE_RELATED.PRIMERA_VIVIENDA,
        simulationChanged: false,
        size: SIZE_SLIDER_CONFIG.DEFAULT,
        sliderPreAppraisalMessageContent: true,
        sliderPreAppraisalMessageContinent: true,
        valid: false,
        validUI: false,
    });
};


const validatingPostalCode = postalCode => {
    return postalCode.length === LENGTH_POSTAL_CODE && !isNaN(postalCode);
}

const validateStep = (step, isNextButton = false) => {
    const continent = step.get('continent');
    const content = step.get('content');
    const population = step.get('population');
    const populationValue = step.get('populationValue');
    const populationIsDirty = step.get('populationIsDirty');
    const validPostalCode = validatingPostalCode(step.get('postalCode'));
    const postalCodeIsDirty = step.get('postalCodeIsDirty');
    const hasPopulation = !!populationValue || !!population;
    const valid = !!validPostalCode && continent !== 0 && content !== 0 && hasPopulation;
    return step.merge({
        valid,
        validUI: valid,
        fieldErrors: isNextButton,
        postalCodeError: (isNextButton || postalCodeIsDirty) && !validPostalCode,
        populationError: (isNextButton || populationIsDirty) && !hasPopulation,
        postAppraisalFetchRecommendation: !valid,
    });
};

const validatePostalCodeError = state => {
    const validPostalCode = validatingPostalCode(state.get('postalCode'));
    const postalCodeError = !validPostalCode || !state.get('populationOptions').size;
    return state.merge({
        postalCodeError
    });

}

const validatePopulationError = state => {
    return state.merge({
        populationError: !state.get('populationValue'),
        populationIsDirty: true,
    });
}

const mapPopulationData = (step, populationData) => {
    const populationoptionsSorted = sortBy(populationData, population => population.populationLiterals);
    const populationOptions = map(populationoptionsSorted, population => {
        return {
            value: population.id,
            label: toCamelCase(population.populationLiterals),
            populationCode: population.populationCode
        };
    });

    const postalCodeError = populationOptions.length === 0;

    let populationValue;
    let populationLabel;
    if (populationOptions.length === 1) {
        populationValue = populationOptions[0].value;
        populationLabel = populationOptions[0].label;
    } else {
        const populationString = step.get('population');
        populationLabel = populationString ?
            get(populationOptions.find(population => populationString === population.label), 'label', null) : null;
        populationValue = populationString ?
            get(populationOptions.find(population => populationString === population.label), 'value', null) : null;
    }

    return step.merge({
        isFetchingSelectPopulation: false,
        postalCodeError,
        populationValue,
        populationLabel,
        populationOptions,
        populationError: false
    });
}

const insuranceRequestRelatedRNCWizardStep2 = (state = setInitialState(), action) => {
    switch (action.type) {
        case INSURANCE_REQUEST_RELATED_RNC_WIZARD_VALIDATE_STEP:
            return validateStep(state, true);

        case INSURANCE_REQUEST_RELATED_RNC_WIZARD_LOAD_STEP:
            const {
                population,
                propertyCharacter,
                propertyUsage,
                postalCode,
                propertyType,
                size
            } = action.data.home;

            return state.merge({
                content: action.data.content,
                continent: action.data.continent,
                immPostalcode: postalCode,
                population,
                postalCode,
                propertyCharacter,
                propertyType,
                propertyUsage,
                size,
                valid: FLOW_STEPS[action.data.flowData.uiStep] > FLOW_STEPS.STEP_2,
            });

        case INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_VISIBLE_STEP:
            return validateStep(state);

        case INSURANCE_REQUEST_RELATED_RNC_WIZARD_SET_DATA:
            const partialState = state.merge({
                ...action.data,
                validUI: false,
                valid: action.data.propertyUsage ? state.get('valid') : false,
                simulationChanged: action.data.propertyUsage ? false : true,
            });

            const noValidateStep = action.data.noValidateStep;

            return noValidateStep ? partialState : validateStep(partialState);

        case INSURANCE_REQUEST_RELATED_RNC_SET_MIN_CONTINENT_VALUE:
            return state.merge({
                minContinentValue: action.data.minContinentValue,
            })

        case INSURANCE_REQUEST_RELATED_RNC_POST_APPRAISAL_WIZARD_SET_DATA:
            return state.merge({
                ...action.data,
                validUI: false,
                valid: action.data.propertyUsage ? state.get('valid') : false,
            });

        case INSURANCE_REQUEST_RELATED_RNC_SIMULATION_LINK_S3_CLIENT_SUCCESS:
            return state.merge({
                simulationChanged: false
            });

        case INSURANCE_REQUEST_RELATED_RNC_SELECTED_PLAN:
            return state.merge({
                simulationChanged: false
            });

        case INSURANCE_REQUEST_CHANGE_ERROR_POSTAL_CODE:
            return validatePostalCodeError(state);

        case INSURANCE_REQUEST_CHANGE_ERROR_POPULATION:
            return validatePopulationError(state);

        case INSURANCE_REQUEST_CLEAR_POSTAL_CODE_ERROR:
            return state.merge({
                postalCodeError: false,
                populationError: false
            });

        case INSURANCE_REQUEST_SELECT_POPULATION_REQUEST:
            return state.merge({
                isFetchingSelectPopulation: true
            });

        case INSURANCE_REQUEST_SELECT_POPULATION_FAILURE:
            return state.merge({
                isFetchingSelectPopulation: false,
                errorSelectPlan: action.data.error
            });

        case INSURANCE_REQUEST_SELECT_POPULATION_SUCCESS:
            return mapPopulationData(state, action.data.population);

        case INSURANCE_REQUEST_RELATED_RNC_WIZARD_RESET_STEP:
                return state.merge({
                    valid: false,
                    fieldErrors: false
                });

        default:
            return state;
    }
};

module.exports = insuranceRequestRelatedRNCWizardStep2;
